<script setup lang="ts">
import { ref } from 'vue';
import { useMutation } from '@tanstack/vue-query';
import { patientTasksApi, type PatientTask } from '@/api/patient-tasks';

interface Props {
  patientTask: PatientTask;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const submitButtonDisabled = ref(false);

const undiscardMutation = useMutation({
  mutationFn: () => patientTasksApi.undiscard(props.patientTask.id),
  onSuccess: () => {
    emit('success');
    emit('close');
  },
  onError: () => {
    submitButtonDisabled.value = false;
  },
});

function handleSubmit() {
  submitButtonDisabled.value = true;
  undiscardMutation.mutate();
}
</script>
<template>
  <v-card class="pa-4">
    <v-card-title>
      Desarchivar Tarea "{{ patientTask.taskDefinitionName }}"
    </v-card-title>
    <v-card-text>
      <p class="mb-4">
        ¿Está seguro que desea desarchivar la tarea "{{ patientTask.taskDefinitionName }}"?
      </p>
      <p class="mb-4 text-body-2 text-medium-emphasis">
        La tarea será desarchivada y volverá a aparecer en la lista de tareas activas del paciente.
      </p>
      <v-alert
        type="info"
        variant="tonal"
        class="mb-4"
      >
        <strong>Categoría:</strong> {{ patientTask.taskCategoryName }}<br>
        <strong>Hora programada:</strong> {{ patientTask.humanScheduledTime }}<br>
        <strong>Asignado por:</strong> {{ patientTask.assignedByEmail }}<br>
        <strong>Archivada el:</strong> {{ patientTask.humanDiscardedAt }}
        <div v-if="patientTask.observations">
          <strong>Observaciones:</strong> {{ patientTask.observations }}
        </div>
      </v-alert>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="undiscardMutation.isPending.value"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="success"
        :loading="undiscardMutation.isPending.value"
        :disabled="submitButtonDisabled"
        @click="handleSubmit"
      >
        Desarchivar
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="undiscardMutation.isError.value"
      type="error"
      variant="tonal"
      class="mt-4"
    >
      Ha ocurrido un error al desarchivar la tarea. Intenta nuevamente.
    </v-alert>
  </v-card>
</template>
